import { I18NextService } from 'angular-i18next';
import { GuidEmpty } from 'mbs-ui-shared@constants';
import { CompanyDestination } from 'mbs-ui-shared@models/Company';
import { PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { StorageAccountLegacy } from 'mbs-ui-shared@models/StorageAccount';
import { SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { StorageType, TypesForFFI } from 'mbs-ui-shared@models/StorageType.enum';
import { FullBackupData, IncrementalData, ScheduleStepValue } from 'mbs-ui-shared@models/backup/schedule-models';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { AdvancedStorageSettings, SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { AdvancedOptionsFlags } from 'wizards/models/advanced-options-models';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { PlansWizardMode } from 'wizards/models/base/base-plan-models';
import { DefaultRetentionPeriodValues } from 'wizards/models/retention-policy-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import {
  getAdvancedScheduleDataInfo,
  getDefaultFullSchedule,
  getDefaultIncrementalSchedule
} from 'wizards/steps/schedule-step/NBF/schedule-step.component.utils';
import { CompanyBackupTemplate } from '../models/company-wizard-models';

const BORROWED_KEYS = [
  'wizardMode',
  'mode',
  'type',
  'isNBF',
  'isUnix',
  'isLinux',
  'isMac',
  'isNetBuild',
  'isOffline',
  'hid',
  'backupVersion',
  'backupVersionUpdated',
  'validVersionForSchedule'
] as const;

export type CompanyWizardPlanContext = {
  backupVersion: string;
  isLinux?: boolean;
  mode?: PlanMode;
  isMac?: boolean;
  isNBF?: boolean;
  isUnix?: boolean;
  type: PlanTypes;
  wizardMode?: PlansWizardMode;
};

// A company template has no agent behind it, so by default the step runs in the same mode PlansTabComponent uses for RD
// configurations - that is what hides the hybrid scheme and its agent-only paths.
export function borrowPlanContext(mainService: RemoteManagementWizardsService, context: CompanyWizardPlanContext): () => void {
  const snapshot = BORROWED_KEYS.reduce((acc, key) => ({ ...acc, [key]: mainService[key] }), {});

  Object.assign(mainService, {
    hid: '',
    isLinux: !!context.isLinux,
    isMac: !!context.isMac,
    isNBF: !!context.isNBF,
    // A template is applied to whatever agent the company installs later, so the .NET Unix build can never be assumed
    isNetBuild: false,
    isOffline: false,
    isUnix: !!context.isUnix,
    mode: context.mode ?? PlanMode.create,
    type: context.type,
    wizardMode: context.wizardMode ?? PlansWizardMode.forRD
  });

  // updateVersionString bails out on an empty version, which would leave the previous wizard's agent behind the steps
  mainService.backupVersion = context.backupVersion;
  mainService.backupVersionUpdated = '';
  mainService.validVersionForSchedule = false;
  mainService.updateVersionString();

  // initBaseDataInService only assigns the keys it is given, so anything left behind would leak into the next plan wizard
  return () => Object.assign(mainService, snapshot);
}

// TODO: MBS-32573 Extend /api/companies/id
export function storageAccountByBucket(availableStorages: SAWithDestinations[], bucketId: string): SAWithDestinations {
  return (availableStorages ?? []).find(({ buckets }) => buckets?.some(({ id }) => id === bucketId));
}

export function companyDestinationsToStorages(destinations: CompanyDestination[], availableStorages: SAWithDestinations[] = []): Storages {
  const connections = (destinations ?? []).map((destination) => {
    const account = storageAccountByBucket(availableStorages, destination.bucketId);
    const bucket = account?.buckets?.find(({ id }) => id === destination.bucketId);
    const storageType = account?.storageType ?? destination.storageAccountType;

    return {
      DisplayName: destination.displayDestinationName,
      ID: destination.bucketId,
      ImmutabilityMode: bucket?.immutabilityMode,
      ImmutabilityPeriodDays: bucket?.immutabilityPeriodDays,
      IsLocal: storageType === StorageType.LocalFS,
      MinimumStorageDuration: account?.MinimumStorageDuration ?? null,
      S3CompatibleSynthetic: account?.S3CompatibleSynthetic,
      StorageAccountName: destination.displayStorageName,
      StorageAccountType: destination.displayStorageName,
      StorageType: storageType
    } as StorageConnection;
  });

  return {
    CloudStorages: connections.filter((connection) => !connection.IsLocal),
    LocalStorages: connections.filter((connection) => connection.IsLocal)
  };
}

export function companyStorageConnections(
  destinations: CompanyDestination[],
  availableStorages: SAWithDestinations[] = []
): StorageConnection[] {
  const storages = companyDestinationsToStorages(destinations, availableStorages);

  return [...storages.CloudStorages, ...storages.LocalStorages];
}

export function toLegacyStorageAccounts(availableStorages: SAWithDestinations[]): StorageAccountLegacy[] {
  return (availableStorages ?? []).map(
    (account) =>
      ({
        Buckets: (account.buckets ?? []).map((bucket) => ({
          BucketName: bucket.bucketName,
          DestinationName: bucket.destinationName,
          Id: bucket.id,
          ImmutabilityMode: bucket.immutabilityMode,
          Region: bucket.region,
          RegionDisplayName: bucket.regionDisplayName
        })),
        Id: account.id,
        MinimumStorageDuration: account.MinimumStorageDuration,
        Name: account.name,
        S3CompatibleSynthetic: account.S3CompatibleSynthetic,
        StorageType: account.storageType
      }) as StorageAccountLegacy
  );
}

export function isLocalStorageConnection(connection: StorageConnection): boolean {
  return connection?.StorageType === StorageType.LocalFS;
}

// Left without an agent version, exactly like the RD configuration wizard PlansTabComponent opens, so the storage list
// gates the same way on both pages
export function planContextFromTemplate(template: CompanyBackupTemplate, mode?: PlanMode): CompanyWizardPlanContext {
  return {
    backupVersion: '',
    isLinux: template.isLinux,
    isMac: template.isMac,
    isNBF: true,
    isUnix: template.isUnix,
    mode,
    type: template.planType
  };
}

export const TemplateTargetAgentVersion = '8.1.0';

// forRMM rather than forRD: the RD branch of the schedule step permanently shows the "agent is too old for FFI" alert,
// which makes no sense for a template, so it is traded for a pinned target version
export function scheduleContextFromTemplate(template: CompanyBackupTemplate, mode?: PlanMode): CompanyWizardPlanContext {
  return {
    ...planContextFromTemplate(template, mode),
    backupVersion: TemplateTargetAgentVersion,
    wizardMode: PlansWizardMode.forRMM
  };
}

export type TemplateStorageContext = {
  advancedStorage?: AdvancedStorageSettings;
  agentVersion: string;
  connection: StorageConnection;
  storages: Storages;
};

export function applyTemplateStorageContext(stepService: WizardStepsService, context: TemplateStorageContext): void {
  const { advancedStorage, agentVersion, connection, storages } = context;
  const badForFFI =
    invalidFFIStorageName(connection, agentVersion) || (advancedStorage?.isStorageBadForFFI ? connection?.DisplayName : null);

  stepService.updateProperty('storage', 'azureAccessTierType', advancedStorage?.azureType ?? null);
  stepService.updateProperty(
    'storage',
    'canShowImmutabilityByStorage',
    SupportMethodsForStepsFromPlan.needShowImmutability(storages?.CloudStorages ?? [], connection?.ID)
  );
  stepService.updateProperty('storage', 'immutabilityPeriodDays', connection?.ImmutabilityPeriodDays);
  stepService.updateProperty('storage', 'invalidFFIStorage', badForFFI);
  stepService.updateProperty('storage', 'isLocal', connection?.IsLocal);
  stepService.updateProperty('storage', 'isPrivateStorage', connection?.IsPrivateStorage);
  stepService.updateProperty('storage', 'minPeriod', SupportMethodsForStepsFromPlan.getStorageMinPeriod(connection, advancedStorage));
  stepService.updateProperty('storage', 'name', connection?.DisplayName);
  stepService.updateProperty('storage', 'storageClass', advancedStorage?.storageClass ?? null);
  stepService.updateProperty('storage', 'type', connection?.StorageType);
}

export function applyTemplateScheduleContext(stepService: WizardStepsService, scheduleStep: ScheduleStepValue): void {
  const scheduleType = scheduleStep?.ScheduleType;

  stepService.updateProperty('schedule', 'isFFI', scheduleType === 'ffi');
  stepService.updateProperty('schedule', 'isSimpleMode', scheduleType === 'ffi');
  stepService.updateProperty('schedule', 'isAdvancedMode', scheduleType === 'predefined');
  stepService.updateProperty('schedule', 'isManualMode', scheduleType === 'noschedule');
  stepService.updateProperty('schedule', 'incrementalDataEnabled', !!scheduleStep?.incrementalData?.enabled);
  stepService.updateProperty('schedule', 'incrementalSchedule', scheduleStep?.incrementalData ?? null);
  stepService.updateProperty('schedule', 'fullSchedule', scheduleStep?.fullBackupData ?? null);
}

export function templateRetentionForSchedule(
  template: CompanyBackupTemplate,
  scheduleStep: ScheduleStepValue
): Partial<Pick<CompanyBackupTemplate, 'destinationPlusRetention'>> {
  const destinationPlusRetention = template.destinationPlusRetention;
  const retention = destinationPlusRetention?.retentionPolicyStep;
  const scheduleType = scheduleStep?.ScheduleType;
  const leavesDefaultRetention = scheduleType === 'ffi' && !!retention?.RetentionUseDefaultSettings;

  if (!retention || !['ffi', 'noschedule'].includes(scheduleType)) return {};

  if (!retention.GFSPolicySettings?.enabled && !leavesDefaultRetention) return {};

  return {
    destinationPlusRetention: {
      ...destinationPlusRetention,
      retentionPolicyStep: {
        ...retention,
        ...(leavesDefaultRetention && { ...DefaultRetentionPeriodValues, RetentionUseDefaultSettings: false }),
        GFSPolicySettings: { ...retention.GFSPolicySettings, enabled: false }
      }
    }
  };
}

export function templateAdvancedSchedule(
  template: CompanyBackupTemplate,
  i18n: Pick<I18NextService, 't'>
): Pick<CompanyBackupTemplate, 'schedule' | 'scheduleStep'> {
  const { fullBackupData, incrementalData } = template.scheduleStep;
  const full = withDefaultWeekDays(fullBackupData, getDefaultFullSchedule().weeklyDayOfWeek.getRawValue());
  const scheduleStep: ScheduleStepValue = {
    ...template.scheduleStep,
    ScheduleType: 'predefined',
    fullBackupData: {
      ...full,
      enabled: true,
      repeatEveryCount: !full.repeatEveryCount && (!template.isLinux || full.recurringPeriod === 'Monthly') ? 1 : full.repeatEveryCount,
      startFromDate: full.startFromDate ?? incrementalData?.startFromDate ?? new Date()
    },
    incrementalData: withDefaultWeekDays(incrementalData, getDefaultIncrementalSchedule().weeklyDayOfWeek.getRawValue())
  };
  const schedule = [
    getAdvancedScheduleDataInfo(scheduleStep.incrementalData, 'incrementalData', i18n),
    getAdvancedScheduleDataInfo(scheduleStep.fullBackupData, 'fullBackupData', i18n)
  ].join('; ');

  return { schedule, scheduleStep };
}

function withDefaultWeekDays<T extends IncrementalData | FullBackupData>(data: T, defaultWeekDays: boolean[]): T {
  return data.weeklyDayOfWeek?.some(Boolean) ? data : { ...data, weeklyDayOfWeek: defaultWeekDays };
}

export function templateAdvancedOptionsNeedShow(flags: AdvancedOptionsFlags, planType: PlanTypes): boolean {
  return SupportMethodsForStepsFromPlan.advancedOptionsNeedShow(flags, planType);
}

export function templateAdvancedOptionsFlags(
  template: CompanyBackupTemplate,
  connection: StorageConnection,
  agentVersion: string
): AdvancedOptionsFlags {
  return SupportMethodsForStepsFromPlan.getStorageAdvancedOptionsFlags(template.advancedOptionsFlags, {
    agentVersion,
    cloudStorage: connection?.IsLocal ? null : connection,
    isNBF: true,
    isNetBuild: false,
    isUnix: template.isUnix,
    storage: connection,
    updateSynthetic: !template.destinationPlusRetention?.whereBackupStep?.IsHybridBackup
  });
}

export function templateStorageConnection(
  template: CompanyBackupTemplate,
  destinations: CompanyDestination[],
  availableStorages: SAWithDestinations[] = []
): StorageConnection {
  const connectionId = template.destinationPlusRetention?.whereBackupStep?.ConnectionID;

  if (!connectionId || connectionId === GuidEmpty) return null;

  return companyStorageConnections(destinations, availableStorages).find(({ ID }) => ID === connectionId) ?? null;
}

// The same gates the plan wizards put on FFI: storage type, the Amazon class or Azure tier, and the synthetic full option
export function templateSupportsFFI(template: CompanyBackupTemplate, connection: StorageConnection): boolean {
  const agentVersion = TemplateTargetAgentVersion.split('.').join('');

  if (!connection || invalidFFIStorageName(connection, agentVersion)) return false;

  const flags = templateAdvancedOptionsFlags(template, connection, agentVersion);
  const syntheticFullOff = !!flags.SyntheticFullSettingPossible && template.advancedOptions?.SyntheticFull === false;

  return !templateAdvancedStorage(template, flags).isStorageBadForFFI && !syntheticFullOff;
}

// Advanced options keep the class / tier of a previous storage after a switch, so only what the current storage offers counts
export function templateAdvancedStorage(template: CompanyBackupTemplate, flags: AdvancedOptionsFlags): AdvancedStorageSettings {
  const options = template.advancedOptions;
  const storageClass = flags?.StorageClassSettingPossible ? (options?.StorageClass ?? null) : null;
  const azureType = flags?.AzureAccessTierType ? (options?.azureAccessTier ?? null) : null;

  return {
    azureType,
    isStorageBadForFFI: StepsHelpers.isStorageBadForFFI(storageClass, 'Amazon') || StepsHelpers.isStorageBadForFFI(azureType, 'Azure'),
    storageClass
  };
}

export function invalidFFIStorageName(connection: StorageConnection, agentVersion: string): string {
  if (!connection) return null;

  return TypesForFFI.isSupported(connection, agentVersion) ? null : connection.DisplayName;
}
